import { Project, SavedRig } from '@/types/project';
import { CACHE_KEYS } from '@/lib/cacheUtils';

const STORAGE_KEY = CACHE_KEYS.GUEST_PROJECTS;

// Storage abstraction layer - swap this for Supabase later
export const storage = {
  // Projects
  getProjects: (): Project[] => {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  },

  saveProjects: (projects: Project[]): void => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
  },

  getProject: (projectId: string): Project | undefined => {
    const projects = storage.getProjects();
    return projects.find(p => p.id === projectId);
  },

  createProject: (name: string): Project => {
    const projects = storage.getProjects();
    const newProject: Project = {
      id: crypto.randomUUID(),
      name,
      rigs: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    projects.push(newProject);
    storage.saveProjects(projects);
    return newProject;
  },

  updateProject: (projectId: string, updates: Partial<Omit<Project, 'id' | 'createdAt'>>): Project | undefined => {
    const projects = storage.getProjects();
    const index = projects.findIndex(p => p.id === projectId);
    if (index === -1) return undefined;
    
    projects[index] = {
      ...projects[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    storage.saveProjects(projects);
    return projects[index];
  },

  deleteProject: (projectId: string): boolean => {
    const projects = storage.getProjects();
    const filtered = projects.filter(p => p.id !== projectId);
    if (filtered.length === projects.length) return false;
    storage.saveProjects(filtered);
    return true;
  },

  // Rigs within a project
  addRig: (projectId: string, rig: Omit<SavedRig, 'id' | 'createdAt' | 'updatedAt'>): SavedRig | undefined => {
    const project = storage.getProject(projectId);
    if (!project) return undefined;

    const newRig: SavedRig = {
      ...rig,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    project.rigs.push(newRig);
    storage.updateProject(projectId, { rigs: project.rigs });
    return newRig;
  },

  updateRig: (projectId: string, rigId: string, updates: Partial<Omit<SavedRig, 'id' | 'createdAt'>>): SavedRig | undefined => {
    const project = storage.getProject(projectId);
    if (!project) return undefined;

    const rigIndex = project.rigs.findIndex(r => r.id === rigId);
    if (rigIndex === -1) return undefined;

    project.rigs[rigIndex] = {
      ...project.rigs[rigIndex],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    storage.updateProject(projectId, { rigs: project.rigs });
    return project.rigs[rigIndex];
  },

  deleteRig: (projectId: string, rigId: string): boolean => {
    const project = storage.getProject(projectId);
    if (!project) return false;

    const filtered = project.rigs.filter(r => r.id !== rigId);
    if (filtered.length === project.rigs.length) return false;

    storage.updateProject(projectId, { rigs: filtered });
    return true;
  },

  duplicateRig: (projectId: string, rigId: string): SavedRig | undefined => {
    const project = storage.getProject(projectId);
    if (!project) return undefined;

    const rig = project.rigs.find(r => r.id === rigId);
    if (!rig) return undefined;

    return storage.addRig(projectId, {
      name: `${rig.name} (Copy)`,
      camera: rig.camera,
      accessories: [...rig.accessories],
      rigStyle: rig.rigStyle,
      autoAddedCables: { ...rig.autoAddedCables },
    });
  },
};
